import React, { FC, useState, useMemo, useCallback } from 'react';
import { Drawer, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Link, NavigateFunction, Params, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { isEqual } from 'underscore';
import { withRouter } from '@/hooks/withRouter';
import { RootState } from '@/index';
import { ArrowForwardIosRounded, DashboardOutlined, FeedbackOutlined, MapOutlined, ViewTimelineOutlined, EditOutlined } from '@mui/icons-material';
import { useInteractiveFlag } from '@/hooks/useInteractiveFlag';
import i18next from 'i18next';

const useStyles = makeStyles(theme => ({
    drawer: {
        flexShrink: 0,
        whiteSpace: 'nowrap',
        overflowX: 'hidden',
    },
    drawerOpen: {
        width: 220,
        overflowX: 'hidden',
        backgroundColor: 'white',
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    drawerClose: {
        top: 64,
        backgroundColor: 'white',
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        overflowX: 'hidden',
        width: 80,
    },
    iconTextWrapper: {
        display: 'flex',
        alignItems: 'center',
        transition: 'all 0.3s ease-in-out',
        width: '100%',
        '&:hover': {
            color: theme.palette.primary.main,
        },
    },
    miniLink: {
        padding: '16px 24px',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        width: '100%',
        textDecoration: 'none',
        '&:hover': {
            backgroundColor: '#f59b7d36',
        },
        '&:hover $iconTextWrapper': {
            color: theme.palette.primary.main,
        },
    },
    feedlink: {
        padding: '16px 24px',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        textDecoration: 'none',
        justifyContent: 'center',
        bottom: '60px',
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        '&:hover': {
            backgroundColor: '#f59b7d36',
        },
        '&:hover $iconTextWrapper': {
            color: theme.palette.primary.main,
        },
    },
    generalIcon: {
        height: '14px',
        width: '14px,'
    },
    iconWrapper: {
        width: 40,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: '10px',
    },
    name: {
        fontSize: '16px',
        marginLeft: '15px',
        color: 'grey',
    },
    selected: {
        backgroundColor: '#f7b6a0',
        color: 'white',
        borderLeft: `5px solid ${theme.palette.primary.main}`,
        '& $iconTextWrapper': {
            color: 'white',
        },
        '& $name': {
            color: theme.palette.primary.main,
        },
    },
    backButton: {
        position: 'absolute',
        top: 6,
        left: '12px',
        zIndex: 1000,
        transition: 'transform 0.3s ease-in-out',
        color: '#44546f',
        '&:hover': {
            '& .MuiSvgIcon-root': {
                color: 'white',
            },
        },
    },
    backButtonRotated: {
        transform: 'rotate(180deg)',
    },
    openBackButton: {
        position: 'fixed',
        top: 80,
        left: 205,
        width: 25,
        height: 25,
        backgroundColor: 'white',
        border: '1px solid grey',
        borderRadius: '100%',
        zIndex: 10000,
        '&:hover': {
            backgroundColor: theme.palette.primary.main,
            border: 'none'
        },
    },
    closedBackButton: {
        position: 'fixed',
        top: 72,
        left: 66,
        width: 25,
        height: 25,
        backgroundColor: 'white',
        border: '1px solid grey',
        borderRadius: '100%',
        zIndex: 10000,
        '&:hover': {
            backgroundColor: theme.palette.primary.main,
            border: '0'
        },
    },
    miniSideBarWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingTop: '45px',
        flexGrow: 1,
        overflowY: 'auto',
    },
    paperStyle: {
        position: 'absolute',
        top: '64px',
    },
    drawerContentWrapper: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'space-between',
    }
}));

export type SidebarProps = {
    router: {
        location: Location;
        navigate: NavigateFunction;
        params: Params;
    };
};

const Sidebar: FC<SidebarProps> = (props) => {
    const classes = useStyles();
    //const { location } = props.router;
    const location = useLocation();
    const { ui, instance: { customername }, selected_process } = useSelector(
        (state: RootState) => state.core,
        isEqual
    );
    const { singleViewData, multiRouteFocus } = useSelector(
        (state: RootState) => state.routes,
        isEqual
    );

    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<string | null>(null);
    const { enabled: interactiveFrontendEnabled } = useInteractiveFlag();
    const isActive = (feature: string) => !ui.includes(feature);
    const handleSelection = (route: string) => {
        setSelected(route);
    };
    const activeRoute = useCallback((path: string) => location.pathname.indexOf(path) > -1, [location.pathname]);
    const isMapViewActive = useMemo(() => activeRoute(`/${customername}routes/routelistmap/${selected_process?.optimization_id}`)
        || activeRoute(`/${customername}routes/${singleViewData?.route?.id}`)
        || activeRoute(`/${customername}routes/${multiRouteFocus[0]}`),
        [activeRoute, customername, singleViewData?.route?.id, selected_process?.optimization_id, multiRouteFocus]);

    const handleBackButton = () => {
        setOpen(!open);
    };

    return (
        <>
            <Drawer
                variant="permanent"
                anchor="left"
                className={`${classes.drawer} ${open ? classes.drawerOpen : classes.drawerClose}`}
                classes={{
                    paper: `${classes.paperStyle} ${open ? classes.drawerOpen : classes.drawerClose}`,
                }}
            >
                <IconButton
                    onClick={handleBackButton}
                    className={`${classes.backButton} ${open ? classes.backButtonRotated : ''} ${open ? classes.openBackButton : classes.closedBackButton}`}
                    color="primary"
                >
                    <ArrowForwardIosRounded className={classes.generalIcon} />
                </IconButton>
                <div className={classes.drawerContentWrapper}>
                    <div className={classes.miniSideBarWrapper}>
                        {isActive('no_dashboard') && (
                            <Link
                                to={`/dashboard/${selected_process?.optimization_id}`}
                                data-cy={'dashboard'}
                                className={`${classes.miniLink} ${activeRoute(`/dashboard/${selected_process?.optimization_id}`) ? classes.selected : ''}`}
                            >
                                <div className={classes.iconTextWrapper}>
                                    <DashboardOutlined
                                        style={{
                                            fontSize: '30px',
                                            color: activeRoute(`/dashboard/${selected_process?.optimization_id}`) ? '#ef5927' : '#999999',
                                        }}
                                    />
                                    {open && (
                                        <span className={classes.name}>
                                            {i18next.t('common:dashboardTitle')}
                                        </span>
                                    )}
                                </div>
                            </Link>
                        )}
                        {isActive('no_routes') && (
                            <Link to={`/routes/timeline/${selected_process?.optimization_id}`}
                                data-cy={'switch-to-timeline'}
                                className={`${classes.miniLink} ${activeRoute(`/routes/timeline/${selected_process?.optimization_id}`) ? classes.selected : ''}`}
                            >
                                <div className={classes.iconTextWrapper}>
                                    <ViewTimelineOutlined
                                        style={{
                                            fontSize: '30px',
                                            color: activeRoute(`/routes/timeline/${selected_process?.optimization_id}`) ? '#ef5927' : '#999999',
                                        }}
                                    />
                                    {open && (
                                        <span className={classes.name}>
                                            {i18next.t('common:timelineTitle')}
                                        </span>
                                    )}
                                </div>
                            </Link>
                        )}
                        <Link
                            to={`/routes/routelistmap/${selected_process?.optimization_id}`}
                            data-cy={'routes-button-map'}
                            className={`${classes.miniLink} ${isMapViewActive ? classes.selected : ''}`}
                        >
                            <div className={classes.iconTextWrapper}>
                                <MapOutlined
                                    style={{
                                        fontSize: '30px',
                                        color: isMapViewActive ? '#ef5927' : '#999999',
                                    }}
                                />
                                {open && (
                                    <span className={classes.name}>
                                        {i18next.t('common:map')}
                                    </span>
                                )}
                            </div>
                        </Link>
                        {interactiveFrontendEnabled && (
                            <Link
                                to={`/interactive/${selected_process?.optimization_id}`}
                                data-cy={'interactive-mode-start'}
                                className={`${classes.miniLink} ${activeRoute('/interactive') ? classes.selected : ''}`}
                            >
                                <div className={classes.iconTextWrapper}>
                                    <EditOutlined
                                        style={{
                                            fontSize: '30px',
                                            color: activeRoute('/interactive') ? '#ef5927' : '#999999',
                                        }}
                                    />
                                    {open && (
                                        <span className={classes.name}>
                                            {i18next.t('common:buttons.interactive')}
                                        </span>
                                    )}
                                </div>
                            </Link>
                        )}
                    </div>
                    <a
                        href="https://smartlane.ai/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.feedlink}
                        onClick={() => {}}
                    >
                        <div className={classes.iconTextWrapper}>
                            <FeedbackOutlined
                                style={{
                                    fontSize: '30px',
                                    color: selected === 'feedback' ? '#ef5927' : '#999999',
                                }}
                            />
                            {open && (
                                <span className={classes.name}>
                                    {i18next.t('common:feedback')}
                                </span>
                            )}
                        </div>
                    </a>
                </div>
            </Drawer>
        </>
    );
};

export default withRouter(Sidebar);
